import { useState, useRef, useEffect, useContext, UIEvent } from 'react';
import { Stack, Typography } from '@mui/material';
import { DynamicOption } from '@refineintel/types';
import styles from './RDropdown.module.css';
import { RTextInput } from '@components/RTextInput/RTextInput';
import { ArrowDownIcon } from '@icons/ArrowDownIcon';
import { ArrowUpIcon } from '@icons/ArrowUpIcon';
import { LocalizationContext } from '@context/LocalizationContext';
import { usePostHog } from 'posthog-js/react';

interface Props {
	options: DynamicOption[];
	setSearchValue: (value: string) => void;
	onSelect: (selectedOption: DynamicOption | null) => void;
	value?: DynamicOption | null;
	isLoading?: boolean;
	isValid?: boolean;
	errorMessage?: string;
	required?: boolean;
	placeholder?: string;
	title?: string;
	onScrolledToBottom?: () => void;
	hasMorePages?: boolean;
}

const highlightText = (text: string, searchTerm: string) => {
	if (!searchTerm) return text;

	const lowerText = text.toLowerCase();
	const lowerSearchTerm = searchTerm.toLowerCase();
	const index = lowerText.indexOf(lowerSearchTerm);

	if (index === -1) return text;

	return (
		<>
			{text.slice(0, index)}
			<strong>{text.slice(index, index + searchTerm.length)}</strong>
			{text.slice(index + searchTerm.length)}
		</>
	);
};

export const RDropdown = ({
	options,
	setSearchValue,
	onSelect,
	value,
	isLoading = false,
	isValid = true,
	errorMessage = 'This field is required',
	required = false,
	placeholder,
	title,
	onScrolledToBottom,
	hasMorePages = false,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState(value?.label || '');
	const [autocompleteOption, setAutocompleteOption] = useState<DynamicOption | null>(null);
	const { DynamicOptionsDropdown } = useContext(LocalizationContext);
	const posthog = usePostHog();
	const dropdownVariant = posthog.getFeatureFlag('naics-dropdown');
	const dropdownV2 = dropdownVariant === 'opened-focused';

	const wrapperRef = useRef<HTMLDivElement>(null);

	const handleScroll = (event: UIEvent<HTMLDivElement>) => {
		const target = event.target as HTMLDivElement;
		const scrollThreshold = 50;

		const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= scrollThreshold;

		if (isNearBottom && onScrolledToBottom && hasMorePages && !isLoading) {
			onScrolledToBottom();
		}
	};

	useEffect(() => {
		const autocompletedOption = options.find((option) => {
			const optionCode = option.label.replace(option.value, '').trim();
			return optionCode === inputValue;
		});
		setAutocompleteOption(autocompletedOption || null);
	}, [options, inputValue]);

	const handleInputChange = (value: string) => {
		setInputValue(value);
		setSearchValue(value.toLowerCase());
		if (value.length > 0) {
			setIsOpen(true);
		} else {
			setIsOpen(false);
		}
	};

	const handleInputClick = () => {
		if (dropdownV2) {
			if (inputValue.length > 0) {
				setIsOpen(!isOpen);
			}
			return;
		}
		setIsOpen(!isOpen);
		return;
	};

	const handleOptionClick = (option: DynamicOption) => {
		onSelect(option);
		setInputValue(option.label);
		setIsOpen(false);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				if (autocompleteOption) {
					handleOptionClick(autocompleteOption);
					setInputValue(autocompleteOption.label);
				}
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [autocompleteOption, inputValue]);

	return (
		<Stack data-cy="pqDropdownContainer" gap={'2px'} width={'100%'}>
			{title && (
				<Typography data-cy="pqDropdownTitle" variant="answerText">
					{title}
				</Typography>
			)}

			<div className={styles.wrapper} ref={wrapperRef}>
				<RTextInput
					autoFocus={dropdownV2}
					data-cy="pqDropdownInput"
					endIcon={isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
					onClick={handleInputClick}
					onChange={handleInputChange}
					value={inputValue}
					placeholder={placeholder}
					isValid={isValid}
					errorMessage={errorMessage}
					required={required}
				/>
				{isOpen && (
					<div data-cy="pqDropdownOptionsContainer" className={styles.dropdown} onScroll={handleScroll}>
						{options.map((option, index) => (
							<div
								key={option.id}
								data-cy={`pqDropdownOption${index}`}
								className={styles.option}
								onClick={() => handleOptionClick(option)}>
								{highlightText(option.label, inputValue)}
							</div>
						))}

						{isLoading && (
							<div data-cy="pqDropdownLoadingState" className={styles.option}>
								<Typography fontSize="14px">{DynamicOptionsDropdown.LoadingText}</Typography>
							</div>
						)}

						{!isLoading && options.length === 0 && (
							<div data-cy="pqDropdownNoResults" className={styles.option}>
								<Typography fontSize="14px">{DynamicOptionsDropdown.NoResultsText}</Typography>
							</div>
						)}
					</div>
				)}
			</div>
		</Stack>
	);
};
